import React from 'react';
import {
  RehearsalCriterion,
  RehearsalLessonItem,
} from '../../../services/quizContentAdminService';
import { Column, FieldRun, resolveField, staleRule } from './shared';
import { QUIZ_REVIEW_NAV_COPY as COPY } from './navCopy';
import InlineEditable from './InlineEditable';
import { EditLine } from './editCoordinator';
import { FIELD_LABELS, QUIZ_REVIEW_EDIT_COPY as EDIT } from './editCopy';
import { ItemPendingChip, useLessonId, useReviewEdit } from './reviewEdit';

/** Saves one criterion field; given only to the English cell inside the page. */
type CriterionSaver = (index: number, field: 'label' | 'description', value: string) => Promise<void>;

const CriterionView: React.FC<{
  c: RehearsalCriterion;
  column: Column;
  /** Position in `r.criteria` (the FULL array — goods and bads together). */
  index: number;
  onSave: CriterionSaver | null;
}> = ({ c, column, index, onSave }) => {
  const tone = c.kind === 'good' ? 'text-emerald-700' : 'text-rose-700';
  const icon = c.kind === 'good' ? '✓' : '✗';
  const lang = column ?? '';
  const label = resolveField(c.label, c.translations, column, 'label');
  const description = resolveField(c.description, c.translations, column, 'description');
  // `example_phrases` is a SCORING field fed to the grader, not learner-facing
  // copy. It is not in TRANSLATABLE_FIELDS and never carries a marker.
  const phrases = c.example_phrases?.trim()
    ? c.example_phrases
        .split('\n')
        .map((p) => p.trim())
        .filter(Boolean)
    : [];
  const editableLine = onSave && (
    // feat-299: label (single-line) and description (multi-line), both required.
    <EditLine as="span" className="block" prefix={<span className={`${tone} font-semibold`}>{icon}</span>}>
      <InlineEditable
        value={c.label ?? ''}
        field="label"
        testId={`inline-criteria-${index}-label`}
        label={FIELD_LABELS.criterionLabel()}
        required
        className="font-medium text-ink"
        onSave={(next) => onSave(index, 'label', next)}
      />
      {/* Always rendered: an empty description is a clickable placeholder (QA-F2). */}
      <span className="text-inkMuted">
        {' — '}
        <InlineEditable
          value={c.description?.trim() ? c.description : ''}
          field="description"
          testId={`inline-criteria-${index}-description`}
          label={FIELD_LABELS.criterionDescription()}
          multiline
          required
          placeholder={EDIT.placeholderCriterionDescription}
          onSave={(next) => onSave(index, 'description', next)}
        />
      </span>
    </EditLine>
  );
  return (
    <li className="mb-1">
      {editableLine || (
        <>
          <span className={`${tone} font-semibold mr-1`}>{icon}</span>
          <FieldRun f={label} language={lang} className="font-medium text-ink" />
          {description.text && (
            <span className="text-inkMuted">
              {' — '}
              <FieldRun f={description} language={lang} />
            </span>
          )}
        </>
      )}
      {phrases.length > 0 && (
        <ul className="list-disc pl-6 text-xs text-inkMuted mt-0.5">
          {phrases.map((p, i) => (
            <li key={i}>"{p}"</li>
          ))}
        </ul>
      )}
    </li>
  );
};

/** `fullHeight` stretches the tinted box to the height of its Both-mode grid
 *  row, so a shorter English cell does not leave a ragged block of colour beside
 *  a taller translated one. Ignored in single-column modes. */
const RehearsalCard: React.FC<{
  r: RehearsalLessonItem;
  index: number;
  column: Column;
  fullHeight?: boolean;
}> = ({ r, index, column, fullHeight }) => {
  // Keep each criterion's index in the FULL array: a criterion save sends the
  // whole set back with only that one entry changed (slice §4, AT-5).
  const indexed = r.criteria.map((c, i) => ({ c, i }));
  const goods = indexed.filter(({ c }) => c.kind === 'good');
  const bads = indexed.filter(({ c }) => c.kind === 'bad');
  const lang = column ?? '';
  const api = useReviewEdit();
  const lessonId = useLessonId();
  const editable = api !== null && column === null;
  const saveCriterion: CriterionSaver | null = editable
    ? // Queued per Rehearsal; the payload is built at send time (QA-F1).
      (i, field, value) => api.saveCriterion(lessonId, r.id, r.criteria, { index: i, field, value })
    : null;
  const name = resolveField(r.name, r.translations, column, 'name');
  const scenario = resolveField(r.scenario_text, r.translations, column, 'scenario_text');
  const meta = (
    <span className="text-xs font-normal text-purple-700">
      · {r.xp_value} XP
      {r.redo_xp > 0 ? ` · ${r.redo_xp} redo` : ''}
      {r.fail_xp > 0 ? ` · ${r.fail_xp} fail` : ''}
      {' '}· role: {r.role} · max {r.max_recording_seconds}s
    </span>
  );
  return (
    <div
      className={`border-l-4 border-purple-400 pl-3 py-1 bg-purple-50/40 rounded-r${
        fullHeight ? ' h-full' : ''
      }`}
    >
      {editable ? (
        <EditLine className="font-semibold text-ink mb-1" prefix={`🎙️ R${index}.`}>
          <InlineEditable
            value={r.name ?? ''}
            field="name"
            label={FIELD_LABELS.rehearsalName()}
            required
            fallback={COPY.unnamedRehearsal}
            onSave={(next) => api.saveRehearsal(lessonId, r.id, { name: next })}
          />{' '}
          {meta}
          <ItemPendingChip entity="rehearsal_template" id={r.id} />
        </EditLine>
      ) : (
        <div className="font-semibold text-ink mb-1">
          🎙️ R{index}.{' '}
          {name.text ? <FieldRun f={name} language={lang} /> : COPY.unnamedRehearsal} {meta}
        </div>
      )}
      {editable && (
        // Rendered even when empty, as a clickable placeholder (QA-F2).
        <div className="text-sm text-ink/90 mb-2 whitespace-pre-wrap break-words">
          <InlineEditable
            value={r.scenario_text ?? ''}
            field="scenario_text"
            label={FIELD_LABELS.scenario()}
            display="block"
            multiline
            required
            placeholder={EDIT.placeholderScenario}
            onSave={(next) => api.saveRehearsal(lessonId, r.id, { scenario_text: next })}
          />
        </div>
      )}
      {!editable && scenario.text && (
        <div
          className={`text-sm text-ink/90 mb-2 whitespace-pre-wrap break-words${staleRule(
            scenario,
          )}${scenario.marker === 'stale' ? ' pl-2' : ''}`}
        >
          <FieldRun f={scenario} language={lang} />
        </div>
      )}
      {goods.length > 0 && (
        <div className="mb-1">
          <div className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">
            Good things to hear
          </div>
          <ul className="list-none pl-1 text-sm">
            {goods.map(({ c, i }, n) => (
              <CriterionView key={`g-${n}`} c={c} column={column} index={i} onSave={saveCriterion} />
            ))}
          </ul>
        </div>
      )}
      {bads.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-rose-700 uppercase tracking-wide">
            Things to avoid
          </div>
          <ul className="list-none pl-1 text-sm">
            {bads.map(({ c, i }, n) => (
              <CriterionView key={`b-${n}`} c={c} column={column} index={i} onSave={saveCriterion} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default RehearsalCard;
